import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { FolderKanban, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, PriorityBadge, StatusBadge, EmptyState, Card } from "@/components/common";
import { relativeTime } from "@/lib/format";

const STATUS_TABS = [
  ["all", "All"], ["in_progress", "In Progress"], ["awaiting_approval", "Awaiting Approval"],
  ["delivered", "Delivered"], ["internal_complete", "Internal"],
];

export default function Jobs() {
  const { companyParam, companyId, companies } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState("all");

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ["jobs", companyId, tab],
    queryFn: async () => (await api.get("/jobs", { params: { ...companyParam, ...(tab !== "all" ? { status: tab } : {}) } })).data,
  });
  const coName = (id) => companies.find((c) => c.id === id)?.name || "—";

  return (
    <div data-testid="jobs-page">
      <PageHeader title="Jobs" subtitle="Every job tracked from directive to delivery, with task trees, agent runs, and evidence." />
      <div className="flex items-center gap-1 mb-4 border-b border-border overflow-x-auto">
        {STATUS_TABS.map(([val, label]) => (
          <button key={val} onClick={() => setTab(val)} data-testid={`jobs-tab-${val}`} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === val ? "border-brand text-brand" : "border-transparent text-ink-muted hover:text-ink"}`}>{label}</button>
        ))}
      </div>
      {isLoading ? <div className="text-sm text-ink-muted">Loading…</div> : jobs.length === 0 ? (
        <EmptyState icon={FolderKanban} title="No jobs in this view" hint="Submit a directive to open a new job." />
      ) : (
        <Card>
          <div className="grid grid-cols-12 gap-3 px-4 py-2 border-b border-border bg-surface-muted text-[10px] uppercase tracking-wider font-semibold text-ink-faint">
            <div className="col-span-4">Job</div><div className="col-span-2">Client</div><div className="col-span-2">Type</div>
            <div className="col-span-1">Priority</div><div className="col-span-2">Status</div><div className="col-span-1 text-right">Progress</div>
          </div>
          <div className="divide-y divide-border">
            {jobs.map((j) => (
              <button key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`job-row-${j.id}`} className="w-full grid grid-cols-12 gap-3 items-center px-4 py-3 text-left hover:bg-surface-muted transition-colors group">
                <div className="col-span-4">
                  <div className="text-sm font-semibold text-ink truncate">{j.name}</div>
                  <div className="text-[11px] text-ink-faint">{coName(j.company_id)} · {relativeTime(j.created_at)}</div>
                </div>
                <div className="col-span-2 text-sm text-ink-muted truncate">{j.client_name}</div>
                <div className="col-span-2 text-xs text-ink-muted">{j.task_label}</div>
                <div className="col-span-1"><PriorityBadge value={j.priority} /></div>
                <div className="col-span-2"><StatusBadge value={j.status} /></div>
                <div className="col-span-1 flex items-center justify-end gap-2">
                  <div className="w-10 h-1.5 bg-surface-muted"><div className="h-full bg-brand" style={{ width: `${j.progress || 0}%` }} /></div>
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
